import React from 'react';
import classNames from 'classnames';

import { invoiceDate } from '@adjust/dashboard-core/lib/format/dateTime';
import { formatNumber } from '@adjust/dashboard-core/lib/format/number';
import { InvoiceType } from '@api/types';
import { InvoiceDownload } from './InvoiceDownload';
import { getLocalizedStatus } from './InvoicesPage.utils';

interface InvoiceDetailsProps {
  totalPriceNet: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  typeLabel: string;
  invoiceType?: InvoiceType;
  number?: string;
  status?: string;
  totalAttributions?: number;
  dueAt?: string;
  downloadUrl?: string;
  usageType?: string;
}

export const InvoiceDetails: React.FC<InvoiceDetailsProps> = ({
  dueAt,
  totalAttributions,
  number,
  totalPriceNet,
  status,
  billingPeriodStart,
  billingPeriodEnd,
  downloadUrl,
  invoiceType,
  typeLabel,
  usageType = 'attributions',
}) => {
  const billingPeriod = `${invoiceDate(billingPeriodStart)} - ${invoiceDate(billingPeriodEnd)}`;
  const isCreditNote = invoiceType === InvoiceType.CreditNote;
  const formattedTotalPriceNet = isCreditNote ? `+${totalPriceNet}` : totalPriceNet;

  const usageTypeLabelMap = {
    attributions: t('label.attributions'),
    maus: t('label.maus'),
    // eslint-disable-next-line @typescript-eslint/naming-convention
    data_points: t('label.data-points'),
  };

  return (
    <li className="invoice-item tile">
      <div className="invoice-header">
        {status && <div className={`invoice-status status-${status}`}>{getLocalizedStatus(status, isCreditNote)}</div>}

        {typeLabel && <div className="invoice-type">{typeLabel}</div>}

        <h2 className={classNames({ 'highlight-price': isCreditNote })}>{formattedTotalPriceNet}</h2>
      </div>

      {number && (
        <div className="invoice-row flex-box-row">
          <div className="row-item-left flex-one">
            <i className="icon-number" />
            <span>{t('label.invoice-no')}</span>
          </div>
          <div className="row-item-right flex-one row-item-number">
            <span title={number}>{number}</span>
          </div>
        </div>
      )}

      <div className="invoice-row flex-box-row">
        <div className="row-item-left flex-one">
          <i className="icon-timespan" />
          <span>{t('label.timespan')}</span>
        </div>
        <div className="row-item-right flex-one">
          <span>{billingPeriod}</span>
        </div>
      </div>

      {dueAt && !isCreditNote && (
        <div className="invoice-row flex-box-row">
          <div className="row-item-left flex-one">
            <i className="icon-due-date" />
            <span>{t('label.due-date')}</span>
          </div>
          <div className="row-item-right flex-one">
            <span>{dueAt}</span>
          </div>
        </div>
      )}

      {totalAttributions !== undefined && !isCreditNote && (
        <div className="invoice-row flex-box-row">
          <div className="row-item-left flex-one">
            <i className="icon-attributions" />
            <span>{usageTypeLabelMap[usageType]}</span>
          </div>
          <div className="row-item-right flex-one">
            <span>{formatNumber(totalAttributions)}</span>
          </div>
        </div>
      )}

      {downloadUrl && <InvoiceDownload downloadUrl={downloadUrl} />}
    </li>
  );
};
